import {
  Component,
  Input,
  HostBinding,
  forwardRef,
  ChangeDetectorRef,
  ElementRef,
  HostListener,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  EmbeddedViewRef,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface PeSelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-pe-select',
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PeSelectComponent),
      multi: true
    }
  ],
  template: `
    <div class="pe-dd" [class.is-open]="open" [class.is-disabled]="disabled" [class.is-compact]="compact">
      <button type="button" class="pe-dd__trigger" [disabled]="disabled" (click)="toggle($event)">
        <span class="pe-dd__value" [class.is-placeholder]="!selectedLabel">
          {{ selectedLabel || placeholder }}
        </span>
        <svg class="pe-dd__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
    </div>
    <ng-template #menu>
      <div
        class="pe-dd__panel"
        [class.is-compact]="compact"
        [style.top.px]="panelTop"
        [style.left.px]="panelLeft"
        [style.min-width.px]="panelWidth"
        [style.width.px]="panelWidth"
        (click)="$event.stopPropagation()">
        <input
          *ngIf="searchable"
          class="pe-dd__search"
          type="text"
          placeholder="Search"
          [value]="query"
          (input)="onQuery($event)" />
        <ul class="pe-dd__list" role="listbox">
          <li
            *ngFor="let option of filtered"
            role="option"
            class="pe-dd__option"
            [class.is-active]="option.value === value"
            (mousedown)="choose($event, option.value)">
            <span>{{ option.label }}</span>
            <svg *ngIf="option.value === value" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </li>
          <li *ngIf="filtered.length === 0" class="pe-dd__empty">No matches</li>
        </ul>
      </div>
    </ng-template>
  `,
  styles: [`
    :host { display: block; width: 100%; min-width: 0; }
    :host.is-compact { width: 5.5rem; }
    .pe-dd { position: relative; width: 100%; }
    .pe-dd__trigger {
      appearance: none !important;
      -webkit-appearance: none !important;
      -moz-appearance: none !important;
      background-image: none !important;
      width: 100%;
      min-height: 2.65rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.55rem;
      padding: 0.5rem 0.85rem;
      border: 1.5px solid #d4dce6;
      border-radius: 0.5rem;
      background: #fff !important;
      font: inherit;
      font-size: 0.875rem;
      font-weight: 500;
      color: #0f172a;
      text-align: left;
      cursor: pointer;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    }
    .pe-dd__trigger::-ms-expand { display: none; }
    .pe-dd__trigger:hover { border-color: #93c5fd; background: #f8fbff !important; }
    .pe-dd__trigger:focus {
      outline: none;
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
    }
    .pe-dd.is-open .pe-dd__trigger { border-color: #2563eb; }
    .pe-dd__value { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
    .pe-dd__value.is-placeholder { color: #94a3b8; font-weight: 500; }
    .pe-dd__chevron { color: #64748b; flex-shrink: 0; pointer-events: none; display: block; }
    .pe-dd.is-open .pe-dd__chevron { transform: rotate(180deg); }
    .pe-dd.is-compact .pe-dd__trigger {
      position: relative;
      min-height: 2.15rem;
      height: 2.15rem;
      justify-content: center;
      padding: 0 1.45rem;
      border-radius: 0.7rem;
    }
    .pe-dd.is-compact .pe-dd__value {
      flex: 0 1 auto;
      text-align: center;
      font-variant-numeric: tabular-nums;
    }
    .pe-dd.is-compact .pe-dd__chevron {
      position: absolute;
      right: 0.45rem;
      top: 50%;
      transform: translateY(-50%);
    }
    .pe-dd.is-compact.is-open .pe-dd__chevron {
      transform: translateY(-50%) rotate(180deg);
    }
    .pe-dd__panel {
      position: fixed;
      z-index: 11050;
      box-sizing: border-box;
      background: #fff;
      border: 1.5px solid #d4dce6;
      border-radius: 1rem;
      box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
      overflow: hidden;
      padding: 0.35rem;
    }
    .pe-dd__search {
      width: 100%;
      border: 1.5px solid #e2e8f0;
      border-radius: 0.7rem;
      padding: 0.55rem 0.75rem;
      margin-bottom: 0.35rem;
      font: inherit;
      font-size: 0.8125rem;
      appearance: none;
      background: #f8fafc;
    }
    .pe-dd__search:focus { outline: none; border-color: #93c5fd; background: #fff; }
    .pe-dd__list {
      margin: 0;
      padding: 0.15rem 0;
      max-height: 16rem;
      overflow-y: auto;
      overflow-x: hidden;
      list-style: none;
      scrollbar-width: thin;
      scrollbar-color: #cbd5e1 transparent;
      -webkit-overflow-scrolling: touch;
    }
    .pe-dd__list::-webkit-scrollbar { width: 6px; }
    .pe-dd__list::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 999px;
    }
    .pe-dd__option {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding: 0.625rem 0.85rem;
      border-radius: 0.65rem;
      font-size: 0.875rem;
      cursor: pointer;
      color: #0f172a;
    }
    .pe-dd__option:hover { background: #f8fafc; }
    .pe-dd__option.is-active { background: #eff6ff; color: #1d4ed8; font-weight: 650; }
    .pe-dd__empty {
      padding: 0.85rem 0.7rem;
      color: #64748b;
      font-size: 0.8125rem;
    }
  `]
})
export class PeSelectComponent implements ControlValueAccessor, OnDestroy {
  @Input() options: PeSelectOption[] = [];
  @Input() placeholder = 'Select';
  @Input() searchable = false;
  @Input() disabled = false;
  @Input() compact = false;

  @ViewChild('menu') menuTpl?: TemplateRef<unknown>;

  @HostBinding('class.is-compact')
  get compactHost(): boolean {
    return this.compact;
  }

  value = '';
  query = '';
  open = false;
  panelTop = 0;
  panelLeft = 0;
  panelWidth = 0;
  private ignoreDocumentClick = false;
  private overlayView?: EmbeddedViewRef<unknown>;
  private overlayEl?: HTMLElement;
  private onScroll = (): void => this.updatePanelPosition();
  private static openInstance: PeSelectComponent | null = null;

  private host = inject(ElementRef<HTMLElement>);
  private cdr = inject(ChangeDetectorRef);
  private vcr = inject(ViewContainerRef);
  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  get filtered(): PeSelectOption[] {
    const q = this.query.trim().toLowerCase();
    if (!q) {
      return this.options;
    }
    return this.options.filter(o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
  }

  get selectedLabel(): string {
    return this.options.find(o => o.value === this.value)?.label || '';
  }

  toggle(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (this.open) {
      this.close();
      return;
    }
    this.openMenu();
  }

  onQuery(event: Event): void {
    this.query = (event.target as HTMLInputElement).value;
    this.overlayView?.detectChanges();
    this.updatePanelPosition();
  }

  choose(event: Event, value: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.close();
  }

  /** Close any open pe-select overlay (e.g. when a modal closes). */
  static closeOpenMenus(): void {
    PeSelectComponent.openInstance?.close();
  }

  @HostListener('window:resize')
  onViewportChange(): void {
    this.updatePanelPosition();
  }

  ngOnDestroy(): void {
    if (PeSelectComponent.openInstance === this) {
      PeSelectComponent.openInstance = null;
    }
    this.detachDocumentClick();
    this.teardownOverlay();
  }

  private openMenu(): void {
    if (PeSelectComponent.openInstance && PeSelectComponent.openInstance !== this) {
      PeSelectComponent.openInstance.close();
    }
    PeSelectComponent.openInstance = this;
    this.query = '';
    this.open = true;
    this.ignoreDocumentClick = true;
    this.mountOverlay();
    this.attachDocumentClick();
    setTimeout(() => {
      this.ignoreDocumentClick = false;
    });
  }

  close(): void {
    if (PeSelectComponent.openInstance === this) {
      PeSelectComponent.openInstance = null;
    }
    this.open = false;
    this.detachDocumentClick();
    this.teardownOverlay();
    this.cdr.markForCheck();
  }

  private onDocumentClick = (event: MouseEvent): void => {
    if (this.ignoreDocumentClick || !this.open) {
      return;
    }
    const target = event.target as Node;
    if (this.host.nativeElement.contains(target) || this.overlayEl?.contains(target)) {
      return;
    }
    this.close();
  };

  private attachDocumentClick(): void {
    this.detachDocumentClick();
    // Capture phase so modal stopPropagation cannot leave the overlay stuck open
    document.addEventListener('click', this.onDocumentClick, true);
  }

  private detachDocumentClick(): void {
    document.removeEventListener('click', this.onDocumentClick, true);
  }

  private mountOverlay(): void {
    this.teardownOverlay();
    if (!this.menuTpl) {
      return;
    }
    this.overlayView = this.vcr.createEmbeddedView(this.menuTpl);
    this.overlayView.detectChanges();
    const node = this.overlayView.rootNodes.find((n): n is HTMLElement => n instanceof HTMLElement);
    if (!node) {
      return;
    }
    this.overlayEl = node;
    document.body.appendChild(node);
    document.addEventListener('scroll', this.onScroll, true);
    this.updatePanelPosition();
    requestAnimationFrame(() => this.updatePanelPosition());
  }

  private teardownOverlay(): void {
    document.removeEventListener('scroll', this.onScroll, true);
    this.overlayEl?.remove();
    this.overlayEl = undefined;
    this.overlayView?.destroy();
    this.overlayView = undefined;
  }

  private updatePanelPosition(): void {
    if (!this.open) {
      return;
    }
    const trigger = this.host.nativeElement.querySelector('.pe-dd__trigger') as HTMLElement | null;
    const rect = (trigger || this.host.nativeElement).getBoundingClientRect();
    const panel = this.overlayEl;
    const gap = 6;
    this.panelWidth = this.compact ? Math.max(rect.width, 7.5 * 16) : Math.max(rect.width, 180);
    this.panelLeft = Math.min(Math.max(8, rect.left), window.innerWidth - this.panelWidth - 8);

    const estimatedHeight = panel?.offsetHeight || (this.options.length * 40 + 16);
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < estimatedHeight + gap && spaceAbove > spaceBelow;

    this.panelTop = openUp
      ? Math.max(8, rect.top - estimatedHeight - gap)
      : rect.bottom + gap;

    if (panel) {
      panel.style.top = `${this.panelTop}px`;
      panel.style.left = `${this.panelLeft}px`;
      panel.style.width = `${this.panelWidth}px`;
      panel.style.minWidth = `${this.panelWidth}px`;
    }
    this.cdr.markForCheck();
  }

  writeValue(value: string): void {
    this.value = value ?? '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
